// src/pages/team/TeamBillingPage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { StatTile } from '@/components/ui/StatTile';

interface TeamBilling {
  credit_pool: number;
  rate_limit_ceiling_rpm: number;
  members_count: number;
  total_spent_30d: number;
}

const TeamBillingPage: React.FC = () => {
  const { data, isLoading } = useQuery<TeamBilling>({
    queryKey: ['team-billing'],
    queryFn: () => apiClient.get('/team/billing').then(r => r.data),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Team billing" description="The credit pool your team shares, and what it has spent." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatTile label="Credit pool" value={data?.credit_pool.toLocaleString() ?? '0'} hint="credits remaining" loading={isLoading} />
        <StatTile label="Spent, last 30 days" value={data?.total_spent_30d.toLocaleString() ?? '0'} hint="credits used" loading={isLoading} />
      </div>

      <Panel title="Limits">
        {isLoading ? (
          <div aria-hidden="true"><div className="skeleton h-5 w-72 max-w-full" /><div className="skeleton mt-3 h-4 w-56 max-w-full" /></div>
        ) : (
          <>
            <p className="text-sm text-foreground">
              Rate limit ceiling: <strong className="font-mono font-medium tabular-nums">{data?.rate_limit_ceiling_rpm} rpm</strong> shared across {data?.members_count} members.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              To top up your credit pool, go to the <Link to="/billing" className="font-medium text-accent-text hover:underline">Billing page</Link>.
            </p>
          </>
        )}
      </Panel>
    </div>
  );
};

export default TeamBillingPage;
